import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaUser,
  FaTag,
  FaClock,
  FaArrowLeft,
  FaShare,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaGlobe,
  FaChevronRight,
} from "react-icons/fa";

import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import SEOHelmet from "../../components/SEOHelmet/SEOHelmet";
import styles from "./SingleBlogPost.module.css";
import blogData from "../../helpers/blogPosts.json";

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featuredImage: string;
  category: string;
  author: string;
  publishedAt: string;
  updatedAt: string;
  readTime: number;
  tags: string[];
  featured: boolean;
  metaDescription: string;
  seoKeywords: string;
}

interface Author {
  id: string;
  name: string;
  title: string;
  bio: string;
  avatar: string;
  specialization: string;
  social?: {
    linkedin?: string;
    twitter?: string;
  };
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
}

const SingleBlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);

  const posts: BlogPost[] = blogData.posts;
  const authors: Author[] = blogData.authors;
  const categories: Category[] = blogData.categories;

  // Find the current post
  const post = posts.find((p) => p.slug === slug);

  // If post not found, redirect to blog page
  useEffect(() => {
    if (!post) {
      navigate("/blog");
      return;
    }
  }, [post, navigate]);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);

    // Scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".scroll-reveal").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  if (!post) {
    return null;
  }

  const author = authors.find((a) => a.id === post.author);
  const category = categories.find((c) => c.id === post.category);

  // Related posts (same category, excluding current post)
  const relatedPosts = posts
    .filter((p) => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = `${post.title} - ${post.excerpt.substring(0, 100)}...`;

    switch (platform) {
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            text
          )}&url=${encodeURIComponent(url)}`
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url
          )}`
        );
        break;
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}`
        );
        break;
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`
        );
        break;
    }
    setShareDropdownOpen(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setShareDropdownOpen(false);
    // You could add a toast notification here
  };

  // Convert markdown content to HTML (enhanced implementation)
  const renderContent = (content: string) => {
    return content
      .replace(/# (.*)/g, "<h1>$1</h1>")
      .replace(/## (.*)/g, "<h2>$1</h2>")
      .replace(/### (.*)/g, "<h3>$1</h3>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/\n\n/g, "</p><p>")
      .replace(/\n/g, "<br/>");
  };

  return (
    <>
      <SEOHelmet
        title={`${post.title} | Nigerian Healthcare Blog`}
        description={post.metaDescription}
        keywords={post.seoKeywords}
        canonicalUrl={`${window.location.origin}/blog/${post.slug}`}
      />
      <Navbar />
      <ScrollToTop />

      <div
        className={`${styles.singleBlogPost} ${isVisible ? styles.fadeIn : ""}`}
      >
        {/* Breadcrumb Navigation */}
        <section className={styles.breadcrumbSection}>
          <div className={styles.container}>
            <nav className={styles.breadcrumb}>
              <Link to="/" className={styles.breadcrumbLink}>
                Home
              </Link>
              <FaChevronRight className={styles.breadcrumbSeparator} />
              <Link to="/blog" className={styles.breadcrumbLink}>
                Blog
              </Link>
              <FaChevronRight className={styles.breadcrumbSeparator} />
              <span className={styles.breadcrumbCurrent}>{post.title}</span>
            </nav>
          </div>
        </section>

        {/* Article Header */}
        <section className={styles.articleHeader}>
          <div className={styles.container}>
            <div className={`${styles.headerContent} scroll-reveal`}>
              <div className={styles.categoryBadge}>
                <FaGlobe className={styles.categoryIcon} />
                {category?.name} • Nigeria Focus
              </div>

              <h1 className={styles.articleTitle}>{post.title}</h1>

              <p className={styles.articleExcerpt}>{post.excerpt}</p>

              <div className={styles.articleMeta}>
                <div className={styles.authorSection}>
                  {author && (
                    <>
                      <img
                        src={author.avatar}
                        alt={author.name}
                        className={styles.authorAvatar}
                      />
                      <div className={styles.authorInfo}>
                        <h4 className={styles.authorName}>{author.name}</h4>
                        <p className={styles.authorTitle}>{author.title}</p>
                        <p className={styles.authorSpecialization}>
                          {author.specialization}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div className={styles.postMeta}>
                  <div className={styles.metaItem}>
                    <FaCalendarAlt className={styles.metaIcon} />
                    <span>{formatDate(post.publishedAt)}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <FaClock className={styles.metaIcon} />
                    <span>{post.readTime} min read</span>
                  </div>
                  <div className={styles.metaItem}>
                    <FaMapMarkerAlt className={styles.metaIcon} />
                    <span>Nigeria</span>
                  </div>
                </div>
              </div>

              <div className={styles.articleActions}>
                <div className={styles.shareDropdown}>
                  <button
                    className={styles.actionBtn}
                    onClick={() => setShareDropdownOpen(!shareDropdownOpen)}
                  >
                    <FaShare />
                    Share
                  </button>

                  {shareDropdownOpen && (
                    <div className={styles.shareMenu}>
                      <button
                        onClick={() => handleShare("twitter")}
                        className={styles.shareOption}
                      >
                        <FaTwitter /> Twitter
                      </button>
                      <button
                        onClick={() => handleShare("linkedin")}
                        className={styles.shareOption}
                      >
                        <FaLinkedin /> LinkedIn
                      </button>
                      <button
                        onClick={() => handleShare("facebook")}
                        className={styles.shareOption}
                      >
                        <FaFacebook /> Facebook
                      </button>
                      <button
                        onClick={() => handleShare("whatsapp")}
                        className={styles.shareOption}
                      >
                        <FaWhatsapp /> WhatsApp
                      </button>
                      <button
                        onClick={copyToClipboard}
                        className={styles.shareOption}
                      >
                        📋 Copy Link
                      </button>
                    </div>
                  )}
                </div>

                <button
                  className={styles.backBtn}
                  onClick={() => navigate("/blog")}
                >
                  <FaArrowLeft />
                  Back to Blog
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className={styles.featuredImageSection}>
          <div className={styles.container}>
            <div className={`${styles.featuredImageContainer} scroll-reveal`}>
              <img
                src={post.featuredImage}
                alt={post.title}
                className={styles.featuredImage}
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className={styles.articleContent}>
          <div className={styles.container}>
            <div className={styles.contentWrapper}>
              <article className={`${styles.mainContent} scroll-reveal`}>
                <div
                  className={styles.articleBody}
                  dangerouslySetInnerHTML={{
                    __html: `<p>${renderContent(post.content)}</p>`,
                  }}
                />

                {/* Tags */}
                <div className={styles.articleTags}>
                  <h4>Related Topics:</h4>
                  <div className={styles.tagsList}>
                    {post.tags.map((tag, index) => (
                      <span key={index} className={styles.tag}>
                        <FaTag /> {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>

              {/* Sidebar */}
              <aside className={`${styles.sidebar} scroll-reveal`}>
                {/* Author Bio */}
                {author && (
                  <div className={styles.authorBio}>
                    <h3>About the Author</h3>
                    <div className={styles.authorCard}>
                      <img
                        src={author.avatar}
                        alt={author.name}
                        className={styles.authorBioAvatar}
                      />
                      <div className={styles.authorBioInfo}>
                        <h4>{author.name}</h4>
                        <p className={styles.authorBioTitle}>{author.title}</p>
                        <p className={styles.authorBioDescription}>
                          {author.bio}
                        </p>
                        {/* {author.social && (
                          <div className={styles.authorSocial}>
                            {author.social.linkedin && (
                              <a
                                href={author.social.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaLinkedin />
                              </a>
                            )}
                            {author.social.twitter && (
                              <a
                                href={author.social.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaTwitter />
                              </a>
                            )}
                          </div>
                        )} */}
                      </div>
                    </div>
                  </div>
                )}

                {/* Nigerian Healthcare Context */}
                <div className={styles.contextBox}>
                  <h3>Nigerian Healthcare Context</h3>
                  <div className={styles.contextContent}>
                    <div className={styles.contextItem}>
                      <FaMapMarkerAlt className={styles.contextIcon} />
                      <span>Serving all 36 Nigerian states</span>
                    </div>
                    <div className={styles.contextItem}>
                      <FaGlobe className={styles.contextIcon} />
                      <span>Focused on African health challenges</span>
                    </div>
                    <div className={styles.contextItem}>
                      <FaUser className={styles.contextIcon} />
                      <span>Written by Nigerian healthcare experts</span>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className={styles.quickStats}>
                  <h3>Article Stats</h3>
                  <div className={styles.statsGrid}>
                    <div className={styles.statItem}>
                      <span className={styles.statNumber}>{post.readTime}</span>
                      <span className={styles.statLabel}>Minutes</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statNumber}>
                        {post.tags.length}
                      </span>
                      <span className={styles.statLabel}>Topics</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statNumber}>🇳🇬</span>
                      <span className={styles.statLabel}>Nigeria</span>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className={styles.relatedSection}>
            <div className={styles.container}>
              <div className={`${styles.sectionHeader} scroll-reveal`}>
                <h2 className={styles.sectionTitle}>
                  Related <span className={styles.textGradient}>Articles</span>
                </h2>
                <p className={styles.sectionSubtitle}>
                  More insights on {category?.name.toLowerCase()} from Nigerian
                  healthcare experts
                </p>
              </div>

              <div className={styles.relatedGrid}>
                {relatedPosts.map((relatedPost, index) => {
                  const relatedAuthor = authors.find(
                    (a) => a.id === relatedPost.author
                  );

                  return (
                    <article
                      key={relatedPost.id}
                      className={`${styles.relatedCard} scroll-reveal`}
                    >
                      <Link
                        to={`/blog/${relatedPost.slug}`}
                        className={styles.relatedLink}
                      >
                        <div className={styles.relatedImage}>
                          <img
                            src={relatedPost.featuredImage}
                            alt={relatedPost.title}
                          />
                        </div>

                        <div className={styles.relatedContent}>
                          <div className={styles.relatedMeta}>
                            <span className={styles.relatedCategory}>
                              {category?.name}
                            </span>
                            <span className={styles.relatedDate}>
                              {formatDate(relatedPost.publishedAt)}
                            </span>
                          </div>

                          <h3 className={styles.relatedTitle}>
                            {relatedPost.title}
                          </h3>
                          <p className={styles.relatedExcerpt}>
                            {relatedPost.excerpt}
                          </p>

                          <div className={styles.relatedFooter}>
                            {relatedAuthor && (
                              <div className={styles.relatedAuthor}>
                                <img
                                  src={relatedAuthor.avatar}
                                  alt={relatedAuthor.name}
                                />
                                <span>{relatedAuthor.name}</span>
                              </div>
                            )}
                            <span className={styles.relatedReadTime}>
                              <FaClock /> {relatedPost.readTime} min
                            </span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  );
                })}
              </div>

              <div className={styles.moreArticles}>
                <Link to="/blog" className={styles.moreArticlesBtn}>
                  View All Nigerian Healthcare Articles
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Newsletter CTA */}
        <section className={styles.ctaSection}>
          <div className={styles.container}>
            <div className={`${styles.ctaContent} scroll-reveal`}>
              <h2 className={styles.ctaTitle}>
                Stay Updated on
                <span className={styles.ctaAccent}> Nigerian Healthcare</span>
              </h2>
              <p className={styles.ctaSubtitle}>
                Get the latest healthcare insights from Nigerian medical experts
                delivered to your inbox weekly.
              </p>
              <div className={styles.ctaActions}>
                <div className={styles.newsletterForm}>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    className={styles.newsletterInput}
                  />
                  <button className={styles.subscribeBtn}>Subscribe Now</button>
                </div>
              </div>
              <div className={styles.ctaNote}>
                <span>
                  Join 25,000+ Nigerians staying informed about healthcare
                  innovations
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default SingleBlogPost;
